import { useState, useCallback, useRef } from 'react';

/**
 * useConfirm - Custom hook for promise-based confirmation dialogs
 * 
 * Replaces window.confirm() with a styled modal that can be awaited.
 * 
 * Usage:
 * ```jsx
 * const { confirm, dialogProps } = useConfirm();
 * 
 * const handleDelete = async () => {
 *   if (await confirm({ message: 'Delete this record?' })) {
 *     // User confirmed
 *     await deleteRecord();
 *   }
 * };
 * 
 * return (
 *   <>
 *     <button onClick={handleDelete}>Delete</button>
 *     <ConfirmDialog {...dialogProps} />
 *   </>
 * );
 * ```
 */
const defaultOptions = {
  title: 'Confirm',
  message: 'Are you sure?',
  confirmText: 'Delete',
  cancelText: 'Cancel',
  type: 'danger',
  wide: false,
};

export const useConfirm = () => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    ...defaultOptions,
  });
  
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({
        isOpen: true,
        ...defaultOptions,
        ...options,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setDialogState((prev) => ({ ...prev, isOpen: false }));
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setDialogState((prev) => ({ ...prev, isOpen: false }));
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
  }, []);

  const dialogProps = {
    isOpen: dialogState.isOpen,
    title: dialogState.title,
    message: dialogState.message,
    confirmText: dialogState.confirmText,
    cancelText: dialogState.cancelText,
    type: dialogState.type,
    wide: dialogState.wide,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };

  return { confirm, dialogProps };
};

export default useConfirm;

